// 時間前注文 Create / Update の write seam (= 個別作成 / 一括作成 / 下書き編集)。
//
// 入力欄・表示・書込 contract (proto `volume_mwh` / VO BidVolumeMw / DB numeric(10,1))
// のすべてが コマ別 MW で、 単位換算は無い (issue #4285。 proto field 名の `_mwh` は
// 旧 spec 時代の歴史的経緯)。 送信値の組み立てをこの 1 関数に閉じるのは spot 側
// `SpotBidding/models/spotBidWrite.ts` と同じ write seam パターン。
import { type MessageInitShape, clone, create } from '@bufbuild/protobuf'
import {
  type Timestamp,
  timestampDate,
  timestampFromDate,
} from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  type BiddingIntradayOrder,
  BiddingIntradayOrderBidMode,
  BiddingIntradayOrderOrderMethod,
  BiddingIntradayOrderScheduleEntryInputSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import {
  type CreateBiddingIntradayOrderRequest,
  CreateBiddingIntradayOrderRequestSchema,
  type UpdateBiddingIntradayOrderRequest,
  UpdateBiddingIntradayOrderRequestSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  formatJstDateTimeLocal,
  parseJstDateTimeLocal,
} from '~/models/dateTime'
import type { FeeAutoConsiderConfirmed } from '~/pages/IntradayBidding/models/feeAutoConsider'
import { isMarketOrderRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayFormulaInput,
  toBiddingIntradayOrderFormula,
} from '~/pages/IntradayBidding/models/intradayFormula'
import type { UpdateIntradayOrderFormInput } from '~/pages/IntradayBidding/models/schema'
import {
  type ScheduleDay,
  resolveScheduleAppliedAt,
} from '~/pages/IntradayBidding/models/strategySchedule'

/**
 * 手数料自動考慮の明示入力 (希望価格 + プレビューで見ていた状態) の request 形。 作成と更新で同じ。
 * OFF は手数料単価を持たない。
 */
const feeAutoConsiderInputOf = (
  desiredPriceYenPerKwh: string,
  confirmed: FeeAutoConsiderConfirmed,
): {
  enabled: boolean
  desiredPriceYenPerKwh: { value: string }
  feeYenPerKwh: { value: string } | undefined
} => ({
  enabled: confirmed.enabled,
  desiredPriceYenPerKwh: { value: desiredPriceYenPerKwh },
  feeYenPerKwh:
    confirmed.feeYenPerKwh !== undefined
      ? { value: confirmed.feeYenPerKwh }
      : undefined,
})

/**
 * 作成する札の価格 (指値 / 成行、 AC-BIN-004d)。
 *
 * - 成行: 価格を持たない。 送信する price_yen_per_kwh は "0" (backend `IntradayOrder`
 *   entity は「成行は price=0」 を不変条件として検証する)
 * - 指値: 入力値 (手数料自動考慮 ON なら希望価格) と、 プレビューで見ていた手数料自動考慮の
 *   状態を必ず組で渡す (AC-BIN-004b)。 入札価格は backend が算出する (#9213)。 状態を
 *   省くと手数料を考慮しない経路で保存されるため、 型で省けないようにしている
 * - 指値 (入力値のまま): 手数料単価を取得できず OFF として扱う画面 (AC-BIN-004b) の入力値を、
 *   そのまま入札価格として price_yen_per_kwh で送る (入札編集の `orderPrice` と同じ)
 * - フォーミュラ (指値): 入力値だけを渡し、 入札価格は backend が保存時に計算する (AC-BIN-020)
 */
export type IntradayOrderCreatePrice =
  | { kind: 'market' }
  | {
      kind: 'limit'
      /** 入力値 (円/kWh、 Decimal string)。 */
      desiredPriceYenPerKwh: string
      feeAutoConsider: FeeAutoConsiderConfirmed
    }
  | {
      kind: 'orderPrice'
      /** 入力値 (円/kWh、 Decimal string)。 そのまま入札価格になる。 */
      orderPriceYenPerKwh: string
    }
  | {
      kind: 'formula'
      formula: IntradayFormulaInput
      /** 手数料単価を取得できず OFF として扱う間 (AC-BIN-004b)。 backend に OFF として計算させる。 */
      treatFeeAutoConsiderAsOff: boolean
    }

export type IntradayOrderCreateInput = {
  targetDate: ProtoDate | undefined
  areaCode: AreaCode
  timeCode: number
  direction: Side
  price: IntradayOrderCreatePrice
  /** 入札量 (MW、 フォーム入力値)。 0.1 MW 刻み (= 制度刻み) 前提。 */
  volumeMw: string
  /** コマ別 GC 時刻 (= Create 必須の GC ガード)。 */
  gcAt: Timestamp
  /**
   * 発注主体 (= どの名義で JEPX に発注するか) の紐付け種別。
   * 以前は札に残らず frontend が表示だけ「発電」固定していた (issue #5366)。
   */
  issuerType: LicenseType
  /**
   * 自動取消時刻 (AC-BIN-017) の `<input type="datetime-local">` 生値
   * ("YYYY-MM-DDTHH:mm")。 未入力 (= 自動取消を予約しない) は `undefined`。
   * 値域 (現在時刻 < 取り下げ時刻 ≦ GC) は `validateWithdrawAt` が入力段で検証済み。
   */
  withdrawAtLocal?: string
  /**
   * 承認モード (AC-BIN-094 / #6650)。 対象エリアの承認フロー設定から
   * `resolveIntradayApprovalMode` (`models/intradayApprovalMode.ts`) で解決した値を
   * 呼び出し側が渡す (= write seam はここでは解決しない。 単一 API に閉じた責務にするため)。
   */
  approvalMode: BiddingApprovalMode
}

/**
 * datetime-local の生値を proto `Timestamp` に変換する。 未入力 / 形式不正は
 * `undefined` を返し、 request からは field ごと落ちる (= optional field 未設定 =
 * 自動取消を予約しない)。 入力欄に出るのは業務上の JST 壁時計 (= 同じ画面の GC・
 * コマ帯と同じ軸) なので端末 TZ ではなく JST として解釈する (`parseJstDateTimeLocal`)。
 * GC (`calcGcAtForKoma`) も入力段の検証 (`validateWithdrawAt`) も JST 壁時計基準
 * なので、 送信値だけローカル解釈にすると非 JST 端末で軸がズレる (issue #6093)。
 */
const toWithdrawAtTimestamp = (
  withdrawAtLocal: string | undefined,
): Timestamp | undefined => {
  if (withdrawAtLocal === undefined) return undefined
  const d = parseJstDateTimeLocal(withdrawAtLocal)
  if (d === undefined) return undefined
  return timestampFromDate(d)
}

/**
 * MW 入力から Create request を組み立てる。 数量は換算せずそのまま載せる。
 * 入力は 0.1 MW 刻みに検証済みのため `toFixed(1)` で桁落ちしない。
 * 承認モードは呼び出し側が対象エリアの承認フロー設定から解決した値をそのまま載せる
 * (AC-BIN-094 / #6650、 `input.approvalMode` 参照)。
 */
export const buildCreateIntradayOrderRequest = (
  input: IntradayOrderCreateInput,
): CreateBiddingIntradayOrderRequest => {
  const { price } = input
  return create(CreateBiddingIntradayOrderRequestSchema, {
    targetDate: input.targetDate,
    areaCode: input.areaCode,
    timeCode: input.timeCode,
    direction: input.direction,
    orderMethod:
      price.kind === 'market'
        ? BiddingIntradayOrderOrderMethod.MARKET
        : BiddingIntradayOrderOrderMethod.LIMIT,
    priceYenPerKwh:
      price.kind === 'market'
        ? { value: '0' }
        : price.kind === 'orderPrice'
          ? { value: price.orderPriceYenPerKwh }
          : undefined,
    feeAutoConsider:
      price.kind === 'limit'
        ? feeAutoConsiderInputOf(
            price.desiredPriceYenPerKwh,
            price.feeAutoConsider,
          )
        : undefined,
    formula:
      price.kind === 'formula'
        ? toBiddingIntradayOrderFormula(price.formula)
        : undefined,
    treatFeeAutoConsiderAsOff:
      price.kind === 'formula' && price.treatFeeAutoConsiderAsOff,
    volumeMwh: { value: Number(input.volumeMw).toFixed(1) },
    approvalMode: input.approvalMode,
    issuerType: input.issuerType,
    gcAt: input.gcAt,
    withdrawAt: toWithdrawAtTimestamp(input.withdrawAtLocal),
  })
}

/**
 * 保存済みの自動取消時刻 → `<input type="datetime-local">` の値 (JST 壁時計の
 * `YYYY-MM-DDTHH:mm`)。 未設定は空文字 (= 自動取消の予約なし)。
 */
export const withdrawAtLocalOf = (ts: Timestamp | undefined): string =>
  ts === undefined ? '' : formatJstDateTimeLocal(timestampDate(ts))

/**
 * 更新する札の価格欄の値の送り方 (AC-BIN-004b・AC-BIN-007。 作成の `IntradayOrderCreatePrice` と同じく、
 * 省略できない判別 union):
 * - `desiredPrice`: 希望価格として明示入力 (プレビューで見ていた手数料自動考慮の状態つき) にして送り、
 *   入札価格は backend が編集後の売/買で算出する。 `price_yen_per_kwh` とは排他
 * - `orderPrice`: 入力値をそのまま入札価格として送る (手数料単価を取得できず OFF として扱う画面)
 * - `formula`: フォーミュラの入力値だけを送り、 入札価格は backend が保存時に計算する (AC-BIN-005 /
 *   AC-BIN-020。 手数料自動考慮の加減算も backend が計算結果に対して行う = AC-BIN-004b)。
 *   価格欄の値は送らない。 手数料単価を取得できず OFF として扱う間は `treatFeeAutoConsiderAsOff` を付ける
 */
export type IntradayOrderUpdatePrice =
  | { kind: 'orderPrice' }
  | { kind: 'desiredPrice'; feeAutoConsider: FeeAutoConsiderConfirmed }
  | {
      kind: 'formula'
      formula: IntradayFormulaInput
      treatFeeAutoConsiderAsOff: boolean
    }

/** Update request の価格の 3 つの入力 (`price_yen_per_kwh` / `fee_auto_consider` / `formula`) のうち、 送る 1 つ。 */
const updatePriceFieldsOf = (
  isMarket: boolean,
  priceYenPerKwh: string,
  price: IntradayOrderUpdatePrice,
): Pick<
  MessageInitShape<typeof UpdateBiddingIntradayOrderRequestSchema>,
  'priceYenPerKwh' | 'feeAutoConsider' | 'formula' | 'treatFeeAutoConsiderAsOff'
> => {
  // 成行は価格を持たないので、 入力値によらず "0" を送る (Create と同じ規約)。
  if (isMarket) return { priceYenPerKwh: { value: '0' } }
  switch (price.kind) {
    case 'formula':
      return {
        formula: toBiddingIntradayOrderFormula(price.formula),
        treatFeeAutoConsiderAsOff: price.treatFeeAutoConsiderAsOff,
      }
    case 'desiredPrice':
      return {
        feeAutoConsider: feeAutoConsiderInputOf(
          priceYenPerKwh,
          price.feeAutoConsider,
        ),
      }
    case 'orderPrice':
      return { priceYenPerKwh: { value: priceYenPerKwh } }
  }
}

/**
 * 下書き編集フォーム (AC-BIN-007) の入力から Update request を組み立てる。
 *
 * `baseline` はフォームを初期化した時点の entity。 画面で編集しない承認モード・発注主体
 * もこの版の値を送り、 楽観ロックの基準 (`expectedUpdatedAt`) も同じ版にそろえる
 * (= フォームに入れた値と別の版の updatedAt で送ると、 他の人の更新を上書きしてしまう)。
 *
 * Update RPC は省略した項目の保存済みの値を保つ。 自動取消時刻は、 触っていなければ
 * 送らない (入力欄は分単位なので、 秒を持つ保存済みの値を送り直すと別の時刻に変わり、
 * 「現在時刻より後」 の判定も受けてしまう)。 入力欄を空にした時だけ clear で外す。
 * メモ・JEPX自由記述欄は入力欄の内容をそのまま送る (空文字なら消える)。
 * 対向条件は戦略モードの札だけの項目 (AC-BIN-114) で、 札が入札方式を持つまで編集口を
 * 出さないので送らない (= 保存済みの値を保つ)。
 * 成行 (AC-BIN-004d) は価格を持たないので、 入札価格は入力値によらず "0" を送る
 * (Create の `buildCreateIntradayOrderRequest` と同じ規約)。
 * 価格欄の値の送り方は `price` (`IntradayOrderUpdatePrice`) で決める。
 */
export const buildUpdateIntradayOrderRequest = (
  baseline: Pick<
    BiddingIntradayOrder,
    | 'id'
    | 'approvalMode'
    | 'issuerType'
    | 'updatedAt'
    | 'orderMethod'
    | 'withdrawAt'
  >,
  values: UpdateIntradayOrderFormInput,
  price: IntradayOrderUpdatePrice,
): UpdateBiddingIntradayOrderRequest => {
  const isMarket = isMarketOrderRow(baseline)
  const withdrawAtTouched =
    values.withdrawAt !== withdrawAtLocalOf(baseline.withdrawAt)
  return create(UpdateBiddingIntradayOrderRequestSchema, {
    id: baseline.id,
    direction: Number(values.side) as Side,
    ...updatePriceFieldsOf(isMarket, values.priceYenPerKwh, price),
    volumeMwh: { value: Number(values.volumeMwh).toFixed(1) },
    approvalMode: baseline.approvalMode,
    issuerType: baseline.issuerType,
    expectedUpdatedAt: baseline.updatedAt,
    withdrawAt: withdrawAtTouched
      ? toWithdrawAtTimestamp(
          values.withdrawAt === '' ? undefined : values.withdrawAt,
        )
      : undefined,
    clearWithdrawAt: withdrawAtTouched && values.withdrawAt === '',
    memoFree: values.memoFree,
    memoJepx: values.memoJepx,
  })
}

/** 戦略モードの 1 段の入力 (= 入札編集モーダルのスケジュール表の 1 行)。 */
export type IntradayScheduleRowInput = {
  day: ScheduleDay
  /** `<input type="time">` の生値 ("HH:mm"、 JST 壁時計)。 */
  timeHm: string
  /** 量 (MW、 フォーム入力値)。 0.1 MW 刻み (= 制度刻み) 前提。 */
  volumeMw: string
  /** 固定価格 (円/kWh、 Decimal string)。 */
  priceYenPerKwh: string
}

export type IntradayOrderStrategyInput = {
  bidMode: 'normal' | 'strategy'
  /** 戦略モードのスケジュール。 通常モードでは送らない。 */
  schedule: IntradayScheduleRowInput[]
  /** 受渡日 (= カレンダー日 `Date`)。 適用日時を前日 / 当日 + 時刻から解決する基準。 */
  deliveryDate: Date
}

/**
 * 下書き編集フォームの Update request に、 入札方式 / スケジュール (AC-BIN-005b) を足す。
 *
 * - 戦略モードの価格・量は backend が最も早い段から導出するので、 ここで計算しない。
 *   本文のフォームが組んだ価格・量はそのまま送る
 * - 適用日時は「前日 / 当日 + HH:mm」 を JST 壁時計として絶対時刻に解決する
 *   (`resolveScheduleAppliedAt`)。 入力段の検証で解決できない行は弾いてある前提で、
 *   解決できなければ例外にする (= 不正な段を黙って落として保存しない)
 */
export const withIntradayStrategySchedule = (
  request: UpdateBiddingIntradayOrderRequest,
  { bidMode, schedule, deliveryDate }: IntradayOrderStrategyInput,
): UpdateBiddingIntradayOrderRequest => {
  const isStrategy = bidMode === 'strategy'
  const next = clone(UpdateBiddingIntradayOrderRequestSchema, request)
  next.bidMode = isStrategy
    ? BiddingIntradayOrderBidMode.STRATEGY
    : BiddingIntradayOrderBidMode.NORMAL
  next.scheduleEntries = isStrategy
    ? schedule.map((row) => {
        const appliedAt = resolveScheduleAppliedAt({
          day: row.day,
          timeHm: row.timeHm,
          deliveryDate,
        })
        if (appliedAt === undefined) {
          throw new Error(`invalid schedule applied time: ${row.timeHm}`)
        }
        return create(BiddingIntradayOrderScheduleEntryInputSchema, {
          appliedAt: timestampFromDate(appliedAt),
          priceYenPerKwh: { value: row.priceYenPerKwh },
          volumeMw: { value: Number(row.volumeMw).toFixed(1) },
        })
      })
    : []
  return next
}
